import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../seo/SEO';
import { SITE_ORIGIN } from '../seo/config';

const PageContainer = styled.section`
  padding: 150px 0 80px;
  background: #ffffff;
`;

const ContentWrapper = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 60px;
  text-align: center;
`;

const Title = styled.h1`
  font-family: 'Tiro Bangla', serif;
  font-size: clamp(26px, 5vw, 38px);
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 2.5px;
  color: #67313a;
  margin-bottom: 18px;
`;

const Sub = styled.p`
  font-family: 'Montserrat', sans-serif;
  color: #a89b97;
  font-size: 12px;
  letter-spacing: 2.2px;
  text-transform: uppercase;
  margin: 0 0 18px;
`;

const Back = styled(Link)`
  display: inline-block;
  margin-top: 26px;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #67313a;
`;

const Intro = styled.p`
  font-family: 'Barlow Semi Condensed', sans-serif;
  font-size: 16px;
  line-height: 1.9;
  color: #6a6a6a;
  max-width: 760px;
  margin: 12px auto 32px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
  margin-top: 18px;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    gap: 10px;
    /* Break out of ContentWrapper side padding to achieve full-bleed images */
    width: calc(100% + 120px);
    margin-left: -60px;
    margin-right: -60px;
  }
`;

const Item = styled.figure`
  margin: 0;
  &.landscape { grid-column: 1 / -1; }
`;

const Img = styled.img`
  width: 100%;
  height: auto;
  display: block;
  box-shadow: 0 16px 36px rgba(0,0,0,0.08);
`;

const PartnersWrap = styled.div`
  max-width: 860px;
  margin: 22px auto 6px;
  text-align: center;
  font-family: 'Montserrat', sans-serif;
  font-size: 12px; /* petite police */
  color: #8a8a8a;
  line-height: 1.8;
`;

// Gallery configuration per slug
const GALLERIES = {
  n1: {
    title: 'Valerie & Keith',
    sub: 'Annecy - Savoie',
    intro: `Valérie and Keith, traveling from Malaysia, chose France to celebrate their union, captivated by its beauty and romantic atmosphere. They began their journey with a week in Provence, discovering the golden light, lavender fields, and timeless villages that embody the French art de vivre, before arriving in Annecy to host their wedding.
In this lakeside town, nestled between mountains and water, they embraced the charm of the old streets and vibrant canals. 
I guided them through Annecy’s picturesque alleys to create a variety of portraits, each capturing both their love and the essence of the place. Their photographs became more than memories: a piece of France to take home, woven into the legacy we crafted together.`,
    images: [
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (1).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (2).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (3).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (4).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (5).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (6).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (7).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (8).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (9).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (10).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (11).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (12).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (13).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (14).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (15).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (17).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (18).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (19).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (20).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (21).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (22).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (16).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (23).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (24).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (25).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (27).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (28).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (29).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (30).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (31).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (32).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (33).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (34).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (35).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (37).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (38).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (39).webp',
      '/images/Valerie_Keith/Lino Ludovic - Wedding photographer - Annecy - Savoie (40).webp',
    ],
  },
  n2: {
    title: 'Claire & Quentin',
    sub: 'Entrevaux - Provence',
    intro: `Claire and Quentin, recently settled in the South of France to embrace its gentle way of life, chose the Domaine de Glandève to seal their union. This vineyard estate, perched high on a rocky outcrop above Cannes, offered a breathtaking backdrop for their wedding.
They exchanged vows in Entrevaux, a fortified village crowned “Most Beautiful Village of France” that same year. 
Nestled among the mountains, the town embodies Provençal charm, where centuries-old olive groves and culinary traditions, from the renowned aglandau olive oil to the local secca delicacy, are deeply rooted in its heritage. Their celebration continued with a romantic couple’s session among the olive trees at the edge of the village, capturing the timeless beauty of Provence and the authenticity of its landscapes.`,
    images: [
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (30).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (31).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (32).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (34).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (35).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (36).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (37).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (38).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (40).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (41).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (42).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (43).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (1).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (4).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (5).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (7).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (6).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (8).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (9).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (10).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (11).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (12).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (13).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (2).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (3).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (15).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (16).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (17).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (18).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (20).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (21).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (22).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (23).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (24).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (25).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (27).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (28).webp',
      '/images/Claire_Quentin/Claire and quentin - wedding photographer France Provence (29).webp',
    ],
  },
  n3: {
    title: 'Adélie & Julien',
    sub: 'Menthon-Saint-Bernard - Savoie',
    intro: `With the allure of top models, Adélie and Julien chose the Palace de Menthon to seal their union, a legendary venue on the shores of Lake Annecy. This 5-star estate, with its history dating back to the 19th century, embodies the quintessence of French elegance, blending Belle Époque charm with the comfort of modern luxury.
Julien, an entrepreneur in the Swiss aeronautics industry, began the day at the Palace Impérial in Annecy, another jewel of haute hospitality, where refinement and discretion set the tone for his preparations. 
Adélie, radiant in her couture gown, joined him later for a celebration where nothing was left to chance. From bottles of Ruinart flowing throughout the evening, to a breathtaking firework display reflected over the lake, every detail was orchestrated to perfection.
Their ceremony, framed by the grandeur of the Palace and the lake’s shimmering backdrop, was as spectacular as it was emotional, a celebration worthy of their love story.`,
    images: [
      '/images/Adélie_et_Julien/00003- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00006- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00017- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00025- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00029- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00049- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00051- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00052- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00061- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00074- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00081- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00087- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00101- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00109- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00123- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00137- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00400- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00143- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00154- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00155- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00160- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00161- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00290- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00292- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00296- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00298- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00308- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00310- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00323- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00381- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00382- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00399- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00461- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00469- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00472- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00507- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00559- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00563- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00564- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00626- Adelie & Julien - MD.webp',
      '/images/Adélie_et_Julien/00630- Adelie & Julien - MD.webp',
    ],
  },
  n4: {
    title: 'Raphaelle & Matthieu',
    sub: 'Château d\'Ailly',
    intro: `Raphaëlle and Matthieu celebrated their wedding at the Château d’Ailly, a timeless gem that has belonged to the same family since 1745. Nestled in the heart of the Roannais, this 80-hectare estate offered an enchanting blend of history, elegance, and countryside charm. For Raphaëlle, deeply attached to her hometown, this château carried the weight of memories and emotions, making it the perfect setting for such a momentous celebration.
    The day unfolded with a rare intensity, radiating love, kindness, and joy at every turn. The floral design, composed in soft yet striking shades of blue, echoed the serene beauty of the château’s surroundings. From the tree-lined avenue leading to the ochre façade, to the refined décor of the reception, every detail was meticulously curated, creating a celebration where heritage and sophistication harmoniously intertwined. It was not just a wedding, but a true ode to elegance and togetherness.`,
    images: [
      '/images/Raphaelle_Matthieu/Compo 1 10x15.webp',
      '/images/Raphaelle_Matthieu/Compo 2 - 10x15.webp',
      '/images/Raphaelle_Matthieu/Compo 3.webp',
      '/images/Raphaelle_Matthieu/LL3_0119.webp',
      '/images/Raphaelle_Matthieu/LL3_0136.webp',
      '/images/Raphaelle_Matthieu/LL3_0178.webp',
      '/images/Raphaelle_Matthieu/LL3_0237.webp',
      '/images/Raphaelle_Matthieu/LL3_0335.webp',
      '/images/Raphaelle_Matthieu/LL3_0366.webp',
      '/images/Raphaelle_Matthieu/LL3_0429.webp',
      '/images/Raphaelle_Matthieu/LL3_0565.webp',
      '/images/Raphaelle_Matthieu/LL3_0594.webp',
      '/images/Raphaelle_Matthieu/LL3_0638.webp',
      '/images/Raphaelle_Matthieu/LL3_0672.webp',
      '/images/Raphaelle_Matthieu/LL3_0802.webp',
      '/images/Raphaelle_Matthieu/LL3_0808.webp',
      '/images/Raphaelle_Matthieu/LL3_0906.webp',
      '/images/Raphaelle_Matthieu/LL3_1003.webp',
      '/images/Raphaelle_Matthieu/LL3_1131.webp',
      '/images/Raphaelle_Matthieu/LL3_1293.webp',
      '/images/Raphaelle_Matthieu/LL3_1357.webp',
      '/images/Raphaelle_Matthieu/LL3_1721.webp',
      '/images/Raphaelle_Matthieu/LL3_1750.webp',
      '/images/Raphaelle_Matthieu/LL3_2130.webp',
      '/images/Raphaelle_Matthieu/LL3_2236.webp',
      '/images/Raphaelle_Matthieu/LL3_2349.webp',
      '/images/Raphaelle_Matthieu/LL3_3102.webp',
      '/images/Raphaelle_Matthieu/LL3_3115.webp',
      '/images/Raphaelle_Matthieu/LL3_3178.webp',
      '/images/Raphaelle_Matthieu/LL3_4038.webp',
      '/images/Raphaelle_Matthieu/LL3_4191.webp',
    ],
  },
  n5: {
    title: 'Tania & Nicolas',
    sub: 'Domaine des Saints Peres',
    intro: `After celebrating their summer wedding, Tania and Nicolas chose to renew their vows in a unique way, during an enchanting winter editorial. Held at the Domaine des Saints Pères, the celebration embraced the magic of Christmas, with refined décor and an intimate ceremony designed especially for them by their original celebrant.
This second chapter allowed them to relive their love story with a new atmosphere, where festive elegance, poetic details, and a sense of timeless wonder came together. A suspended moment, captured with finesse, offering them a memorable vision of their union in two distinct yet complementary times.`,
    images: [
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (1).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (2).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (3).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (7).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (4).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (5).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (6).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (8).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (9).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (10).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (11).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (13).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (14).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (15).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (16).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (17).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (19).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (20).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (21).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (22).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (23).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (24).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (25).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (28).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (29).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (27).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (30).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (32).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (33).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (34).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (35).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (36).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (31).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (37).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (38).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (39).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (40).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (41).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (42).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (43).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (45).webp',
      '/images/Tania_Nicolas/Wedding photography France and worldwide - Lino Ludovic (44).webp',
    ],
  },
  n6: {
    title: 'Maroussia & Charles',
    sub: 'loft 440',
    intro: `During a prestigious wedding showcase, Maroussia embodied the elegance of several couture bridal gowns, captivating the audience with her grace. At her side, Charles, crowned Mister France 2023, brought charisma and refinement, making them an unforgettable duo that marked the event.
Surrounded by an exceptional team of creators and artisans, the atmosphere blended haute couture, fine jewelry, and floral artistry into a celebration of style and sophistication. More than a showcase, it became a living tableau of elegance, where fashion and emotion intertwined.`,
    images: [
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (2).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (3).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (4).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (5).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (6).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (7).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (8).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (9).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (10).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (11).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (12).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (13).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (14).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (15).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (16).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (17).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (18).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (19).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (20).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (21).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (22).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (23).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (24).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (25).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (26).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (28).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (29).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (30).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (31).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (32).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (27).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (33).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (34).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (35).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (36).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (37).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (38).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (39).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (40).webp',
      '/images/Maroussia_Charles/Wedding photographer France - USA - Worldwide (41).webp',
    ],
  },
  n7: {
    title: 'Sarah & Nathaniel',
    sub: 'Château de Matel',
    intro: `Sarah and Nathaniel first shared an intimate engagement session at the Château de la Roche, a romantic prelude that set the tone for their celebration. They then chose to marry close to their families, in the heart of Rhône-Alpes, at the Château de Matel. This 17th-century estate, nestled on the banks of the Loire in Roanne and listed as a historic monument, offered a setting where heritage and elegance blended seamlessly. 
    The château, once home to the Knights Hospitaller, has preserved its grandeur through the centuries. Oak floors, monumental fireplaces and richly adorned rooms whispered stories of the past, while the vast park, with its French gardens, fountains and centuries-old trees, created an atmosphere of timeless poetry. Their wedding unfolded in this refined harmony, where history, family and emotion came together in grace and serenity, leaving memories as enduring as the place that hosted them.`,
    images: [
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (1).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (2).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (3).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (4).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (5).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (6).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (7).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (8).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (9).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (10).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (11).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (12).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (13).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (14).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (15).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (16).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (17).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (18).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (19).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (21).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (22).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (23).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (24).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (25).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (26).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (27).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (28).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (30).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (31).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (32).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (33).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (29).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (34).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (35).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (36).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (38).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (40).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (41).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (42).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (43).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (44).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (46).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (47).webp',
      '/images/Sarah_Nathaniel/Luxury wedding photography France  (48).webp',
    ],
  },
  n8: {
    title: 'Aurélie & David',
    sub: 'Château de Matel',
    intro: `Aurélie and David, a Parisian couple, first invited me to capture their civil wedding a year before their grand celebration, a precious occasion that allowed their grandparents to be part of the joy. When the time came for their religious and festive union, they chose the Château de Matel in Rhône-Alpes, an estate steeped in history and refinement.
Their day unfolded with the energy of celebration and the elegance of detail. A cocktail bar, designed with finesse, became a gathering point where sophistication met conviviality. The ceremony itself carried an intensity that reflected their personalities: vibrant, joyful, and deeply authentic.
Set against the timeless backdrop of the château and its verdant park, their wedding became a living testament to love celebrated without compromise, uniting heritage, harmony and sophistication.`,
    images: [
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (1).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (2).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (3).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (4).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (5).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (6).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (7).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (8).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (9).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (10).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (11).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (12).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (13).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (14).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (15).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (16).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (17).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (18).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (19).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (20).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (21).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (22).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (23).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (24).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (25).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (26).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (27).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (28).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (29).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (30).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (31).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (32).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (33).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (34).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (35).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (36).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (37).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (38).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (39).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (40).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (41).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (42).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (43).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (44).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (45).webp',
      '/images/Aurélie_David/French wedding photographer - Lino Ludovic (46).webp',
    ],
  },
  n9: {
    title: 'Lea & Aurelien',
    sub: 'Six-Fours',
    intro: `Married in Provence, Léa and Aurélien wished to prolong the magic of their wedding with a Day After session. This unique experience allowed them to slip back into their wedding attire and relive the intensity of their union in a new light.
We traveled to the Mediterranean coast, on the wild rocks of l’Île du Gaou in La Seyne-sur-Mer. 
At sunset, the landscape became a stage of poetry, where emotions flowed freely. Freed from the rhythm of the wedding day, they embraced this intimate moment as a space for reconnection, giving their couple’s portraits all the attention they deserved. This session was more than photographs, it was a continuation of their story an experience of beauty, emotion, and timeless elegance.`,
    images: [
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (1).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (2).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (3).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (4).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (5).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (6).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (8).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (7).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (9).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (10).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (11).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (12).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (14).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (13).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (15).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (16).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (18).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (19).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (20).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (25).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (21).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (22).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (23).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (24).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (26).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (27).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (28).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (29).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (17).webp',
      '/images/Lea_Aurelien/Wedding - Day after photoshoot - France (30).webp',
    ],
  },
};

// Partners per gallery (add more keys as needed)
const PARTNERS = {
  n1: [
    { role: 'Photographer', handle: '@linoludovic' },
  ],
  n2: [
    { role: 'Wedding Venue', handle: '@glandeve' },
    { role: 'Photographer', handle: '@linoludovic' },
    { role: 'Flowers', handle: 'Maria Fleurs à Cannes' },
    { role: 'Chef', handle: 'Aux 3 sens' },
    { role: 'Hairdresser', handle: '@leslieazurcoiffure' },
    { role: 'Bride Dress', handle: '@oscarlettparisannecynogent' },
    { role: 'Groom Suit', handle: '@fatherandsonofficiel' },
  ],
  n3: [
    { role: 'Wedding Venue', handle: '@palace_de_menthon' },
    { role: 'Photographer', handle: '@linoludovic' },
    { role: 'Flowers', handle: '@nathaliefleurs.veyrier_du_lac' },
    { role: 'Chef', handle: '@palace_de_menthon' },
    { role: 'DJ', handle: '@bimagency.fr' },
    { role: 'Music band', handle: '@bimagency.fr' },
    { role: 'Hairdresser', handle: '@marinkamakeupartist' },
    { role: 'Bride dress', handle: '@belleenblanc_lausanne' },
  ],
  n4: [
    { role: 'Wedding Venue', handle: 'Château d’Ailly' },
    { role: 'Photographer', handle: '@linoludovic' },
    { role: 'Catering', handle: 'Barriquand' },
    { role: 'Hair', handle: 'Anaïs Vial' },
    { role: 'Makeup', handle: 'Manon Amanieu' },
    { role: 'DJ', handle: 'William Maisse' },
    { role: 'Florals', handle: 'Le Jardin Enchanté' },
    { role: 'Priest', handle: 'Father Guibert' },
    { role: 'Church Organist', handle: 'Vincent Buisse' },
    { role: 'Church Singer', handle: 'Hélène de Fombelle' },
    { role: 'Beer Service', handle: 'My Beers' },
    { role: 'Photo & Audio Booth', handle: 'My Story Booth' },
    { role: 'Babysitter', handle: 'Joséphine Schied' },
  ],
  n5: [
    { role: 'Wedding Venue', handle: '@domaine_des_saints_peres' },
    { role: 'Wedding Planner', handle: '@deuxoui_pour_unnom' },
    { role: 'Celebrant', handle: '@emyceremonies' },
    { role: 'Photographer', handle: '@linoludovic' },
    { role: 'Videographer', handle: '@sunlightwedding' },
    { role: 'Decorator', handle: '@art_deco_receptions' },
    { role: 'Florist', handle: '@lesfleurspapillonnent' },
    { role: 'Customization', handle: '@ateliernylaime' },
    { role: 'Wedding Cake', handle: '@cam_pastry' },
    { role: 'Jewelry', handle: '@moanacreation' },
    { role: 'Models', handle: '@tania_and_family_ & @nico38157' },
    { role: 'Stationery', handle: '@paillettesdemotions' },
    { role: 'Knitwear', handle: '@lelyonquitricote' },
  ],
  n6: [
    { role: 'Celebrant', handle: '@quinzefevrierceremonies' },
    { role: 'Bridal gowns', handle: '@elsagary & @zoecouturewedding' },
    { role: 'Floral accessories', handle: '@laventuredepaul' },
    { role: 'Photography', handle: '@linoludovic' },
    { role: 'Florals', handle: '@desfleursdessaisons' },
    { role: 'Jewelry', handle: '@helenajoy' },
    { role: 'Suits & cocktail dresses', handle: '@aimecommemariage' },
  ],
  n7: [
    { role: 'Wedding Planner', handle: 'anaweddingevent (no longer active)' },
    { role: 'Photographer', handle: '@linoludovic' },
    { role: 'Decoration', handle: 'anaweddingevent (no longer active)' },
    { role: 'Wedding Venue', handle: '@chateaudematel' },
    { role: 'Flowers', handle: '@allons_voir_si_la_rose' },
    { role: 'Chef', handle: '@globe.reception' },
    { role: 'DJ', handle: '@clubbing_events' },
    { role: 'Music Band', handle: '@helloyouduo' },
    { role: 'Hairdresser', handle: '@avialcoiffure' },
    { role: 'Bride Dress', handle: '@nocesblanches71' },
    { role: 'Groom Suit', handle: 'Nino Loretti' },
  ],
  n8: [
    { role: 'Wedding Venue', handle: 'Château de Matel' },
    { role: 'Photographer', handle: '@linoludovic' },
    { role: 'Hair & Makeup', handle: '@maiamaquilleuse' },
    { role: 'Florals & Decoration', handle: '@evendeco42' },
    { role: 'Catering', handle: '@bruno_guerpillon' },
    { role: 'Videography', handle: '@sunlightwedding' },
    { role: 'Entertainment', handle: '@clubbingevents' },
    { role: 'Cocktails', handle: '@labarmobile' },
    { role: 'Gowns', handle: '@elisemartimort, @meryl.suissa' },
    { role: 'Suit', handle: '@samsonsurmesure' },
    { role: 'Live Music', handle: '@tessiamusic_' },
    { role: 'Earrings', handle: '@maisonmassillon' },
    { role: 'Brooch', handle: '@lizeronparis' },
  ],
  n9: [
    { role: 'Photography', handle: '@linoludovic' },
  ],
};

// French translations for gallery intros
const INTROS_FR = {
  n1: `Valérie et Keith, venus de Malaisie, ont choisi la France pour célébrer leur union, séduits par sa beauté et son romantisme. Ils ont commencé leur voyage par une semaine en Provence, à la découverte de la lumière dorée, des champs de lavande et des villages intemporels qui incarnent l’art de vivre à la française, avant de rejoindre Annecy pour leur mariage.
Dans cette ville lacustre, nichée entre montagnes et eaux, ils ont embrassé le charme des ruelles anciennes et des canaux vibrants. Je les ai guidés dans les allées pittoresques d’Annecy pour créer une variété de portraits, capturant à la fois leur amour et l’âme du lieu.
Leurs photographies sont devenues davantage que des souvenirs : un fragment de France à emporter, tissé dans l’héritage que nous avons créé ensemble.`,
  n2: `Installés récemment dans le Sud de la France pour y embrasser sa douceur de vivre, Claire et Quentin ont choisi le Domaine de Glandève pour sceller leur union. Ce domaine viticole, perché sur un éperon rocheux au‑dessus de Cannes, offrait un décor grandiose à leur mariage.
Ils ont échangé leurs vœux à Entrevaux, village fortifié sacré “Plus beau village de France” la même année. Enclavée au cœur des montagnes, la cité incarne le charme provençal, où les oliveraies centenaires et les traditions culinaires — de la célèbre aglandau à la secca locale — sont profondément ancrées.
La célébration s’est prolongée par une séance romantique parmi les oliviers aux abords du village, capturant la beauté intemporelle de la Provence et l’authenticité de ses paysages.`,
  n3: `Avec une allure de top‑models, Adélie et Julien ont choisi le Palace de Menthon pour sceller leur union, lieu emblématique sur les rives du lac d’Annecy. Ce 5 étoiles, dont l’histoire remonte au XIXe siècle, incarne la quintessence de l’élégance française, mêlant charme Belle Époque et confort du luxe contemporain.
Julien, entrepreneur dans l’aéronautique en Suisse, a commencé la journée au Palace Impérial à Annecy, autre joyau de l’hôtellerie, où raffinement et discrétion ont donné le ton de ses préparatifs.
Adélie, rayonnante dans sa robe couture, l’a rejoint plus tard pour une célébration où rien n’a été laissé au hasard. Des flacons de Ruinart servis tout au long de la soirée au feu d’artifice se reflétant sur le lac, chaque détail a été orchestré avec perfection.
Leur cérémonie, encadrée par la majesté du Palace et le miroitement du lac, fut aussi spectaculaire qu’émouvante — à la hauteur de leur histoire.`,
  n4: `Raphaëlle et Matthieu ont célébré leur mariage au Château d’Ailly, joyau intemporel appartenant à la même famille depuis 1745. Niché au cœur du Roannais, ce domaine de 80 hectares offrait un écrin d’histoire, d’élégance et de charme champêtre. Pour Raphaëlle, très attachée à sa ville natale, ce lieu porteur de mémoire s’imposait comme le cadre idéal.
La journée s’est déroulée dans une intensité rare, empreinte d’amour, de bienveillance et de joie. La composition florale, déclinée dans de subtiles nuances de bleu, répondait à la sérénité des environs du château.
De l’allée arborée menant à la façade ocre aux décors raffinés de la réception, chaque détail fut pensé avec soin. Plus qu’un mariage, une ode à l’élégance et au partage.`,
  n5: `Après avoir célébré leur mariage estival, Tania et Nicolas ont choisi de renouveler leurs vœux d’une manière singulière, lors d’un éditorial hivernal enchanteur. Organisée au Domaine des Saints Pères, la célébration embrassait la magie de Noël, avec un décor raffiné et une cérémonie intime imaginée spécialement pour eux par leur célébrante.
Ce second chapitre leur a permis de revivre leur histoire dans une atmosphère nouvelle, où l’élégance festive, les détails poétiques et un doux sentiment d’émerveillement se répondaient. Un moment suspendu, capturé avec finesse, offrant une vision mémorable de leur union à deux temps, complémentaires.`,
  n6: `Lors d’un prestigieux salon du mariage, Maroussia a incarné l’élégance de plusieurs robes de mariée couture, captivant le public par sa grâce. À ses côtés, Charles, sacré Mister France 2023, apportait charisme et raffinement — un duo inoubliable qui a marqué l’événement.
Entourés d’une équipe d’exception — créateurs, joailliers, artisans — l’atmosphère mêlait haute couture, joaillerie et art floral dans une célébration du style et de la sophistication. Plus qu’une présentation, un tableau vivant d’élégance, où mode et émotion se sont entremêlées.`,
  n7: `Sarah et Nathaniel ont d’abord partagé une séance engagement intime au Château de la Roche, prélude romantique à leur célébration. Ils ont ensuite choisi de se marier au plus près de leurs familles, au cœur de la région Rhône‑Alpes, au Château de Matel. Ce domaine du XVIIe siècle, niché sur les rives de la Loire à Roanne et classé monument historique, offrait un cadre où patrimoine et élégance se rencontrent.
Ancien fief des Hospitaliers, le château a traversé les siècles en préservant sa grandeur. Parquets de chêne, cheminées monumentales et salons ornés murmurent des récits d’autrefois, tandis que le vaste parc — jardins à la française, fontaines, arbres centenaires — compose un décor de poésie intemporelle.
Leur mariage s’est déployé dans cette harmonie raffinée, où histoire, famille et émotion se sont rejoints avec grâce et sérénité, laissant des souvenirs aussi durables que le lieu qui les a accueillis.`,
  n8: `Aurélie et David, parisiens, m’ont d’abord confié leur mariage civil un an avant leur grande célébration — un moment précieux qui a permis à leurs grands‑parents d’en être. Pour leur union religieuse et festive, ils ont choisi le Château de Matel en Rhône‑Alpes, un domaine empreint d’histoire et de raffinement.
La journée s’est déroulée avec l’énergie de la fête et l’élégance du détail. Un bar à cocktails, pensé avec finesse, est devenu un point de rencontre où convivialité et sophistication se sont retrouvées. La cérémonie, vibrante et sincère, reflétait pleinement leurs personnalités.
Sur fond de parc verdoyant et d’architecture intemporelle, leur mariage est devenu un témoignage vivant d’un amour célébré sans compromis, unissant héritage, harmonie et sophistication.`,
  n9: `Mariés en Provence, Léa et Aurélien ont souhaité prolonger la magie de leur union avec une séance Day After. Cette expérience unique leur a permis de revêtir à nouveau leurs tenues et de revivre l’intensité de leur lien sous une nouvelle lumière.
Nous avons rejoint la côte méditerranéenne, sur les rochers sauvages de l’Île du Gaou à La Seyne‑sur‑Mer. Au coucher du soleil, le paysage s’est fait scène de poésie, où les émotions se déployaient librement. Libérés du rythme du jour J, ils ont vécu cet instant comme un espace de reconnexion, offrant à leurs portraits de couple toute l’attention qu’ils méritent.
Davantage qu’une séance, une continuité de leur histoire — une expérience de beauté, d’émotion et d’élégance intemporelle.`,
};

// Helper to build SEO slugs from a subtitle (venue/location)
const normalize = (s) => String(s)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '');

const makeSlug = (sub) => {
  if (!sub) return 'wedding_story';
  const base = normalize(sub);
  return `wedding_${base}`;
};
const makeSlugWithTitle = (sub, title) => `wedding_${normalize(sub)}_${normalize(title)}`;

const GalleryPage = () => {
  const { slug: slugParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Support both legacy ids (n1..n9) and SEO slugs (wedding_...)
  let cfg = GALLERIES[slugParam];
  let resolvedKey = slugParam;
  if (!cfg) {
    // Pass 1: prefer exact withTitle match for disambiguation when multiple share same base
    for (const key of Object.keys(GALLERIES)) {
      const candidate = GALLERIES[key];
      const withTitle = makeSlugWithTitle(candidate.sub, candidate.title);
      if (slugParam === withTitle) {
        cfg = candidate;
        resolvedKey = key;
        break;
      }
    }
  }
  if (!cfg) {
    // Pass 2: fallback to base or base-prefixed matching
    for (const key of Object.keys(GALLERIES)) {
      const candidate = GALLERIES[key];
      const base = makeSlug(candidate.sub);
      if (slugParam === base || slugParam.startsWith(base + '_')) {
        cfg = candidate;
        resolvedKey = key;
        break;
      }
    }
  }
  const [orients, setOrients] = useState({});
  const { lang } = useLanguage();

  // Redirect legacy n# routes to their canonical SEO slug
  useEffect(() => {
    const isLegacy = Object.prototype.hasOwnProperty.call(GALLERIES, slugParam) && /^n\d+$/i.test(slugParam);
    if (!isLegacy || !cfg) return;
    const counts = Object.values(GALLERIES).reduce((m, c) => {
      const b = makeSlug(c.sub);
      m[b] = (m[b] || 0) + 1;
      return m;
    }, {});
    const base = makeSlug(cfg.sub);
    const canonical = counts[base] > 1 ? makeSlugWithTitle(cfg.sub, cfg.title) : base;
    if (slugParam !== canonical) {
      const isFr = location.pathname.startsWith('/fr');
      navigate(`${isFr ? '/fr' : ''}/portfolio/${canonical}`, { replace: true });
    }
  }, [slugParam, navigate, cfg, location.pathname]);

  if (!cfg) {
    return (
      <PageContainer>
        <ContentWrapper>
          <Title>{lang === 'fr' ? 'Galerie — Bientôt disponible' : 'Gallery — Coming Soon'}</Title>
          <Sub>{lang === 'fr' ? 'Nous préparons cette histoire. Revenez bientôt.' : 'We are preparing this story. Please check back later.'}</Sub>
          <Back to={`${location.pathname.startsWith('/fr') ? '/fr' : ''}/portfolio`}>{lang === 'fr' ? '← Retour au Portfolio' : '← Back to Portfolio'}</Back>
        </ContentWrapper>
      </PageContainer>
    );
  }

  const onLoad = (src) => (e) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    const kind = w >= h ? 'landscape' : 'portrait';
    setOrients((prev) => ({ ...prev, [src]: kind }));
  };
  const displayIntro = lang === 'fr' ? (INTROS_FR[resolvedKey] || cfg.intro) : cfg.intro;
  const ogImage = (cfg && Array.isArray(cfg.images) && cfg.images.length) ? cfg.images[0] : undefined;
  const schemaLD = cfg ? {
    '@context': 'https://schema.org',
    '@type': 'ImageGallery',
    name: `${cfg.title} — ${cfg.sub}`,
    inLanguage: lang === 'fr' ? 'fr-FR' : 'en-GB',
    image: (cfg.images || []).slice(0, 12).map((p) => p.startsWith('http') ? p : `${SITE_ORIGIN}${p}`)
  } : undefined;

  return (
    <PageContainer>
      <ContentWrapper>
        <SEO
          title={
            lang === 'fr'
              ? `Galerie Mariage — ${cfg.title} | ${cfg.sub} — Lino Ludovic`
              : `Wedding Gallery — ${cfg.title} | ${cfg.sub} — Lino Ludovic`
          }
          description={(displayIntro || '').slice(0, 180)}
          image={ogImage}
          schema={schemaLD}
          breadcrumbs={[
            { name: lang === 'fr' ? 'Portfolio' : 'Portfolio', path: `${location.pathname.startsWith('/fr') ? '/fr' : ''}/portfolio` },
            { name: `${cfg.title}`, path: `${location.pathname}` }
          ]}
        />
        <Title>{cfg.title}</Title>
        <Sub>{cfg.sub}</Sub>
        {(() => {
          const toTwoBlocks = (text) => {
            try {
              const lines = String(text || '')
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean);
              if (lines.length >= 2) {
                const half = Math.ceil(lines.length / 2);
                return [lines.slice(0, half).join(' '), lines.slice(half).join(' ')];
              }
              const words = String(text || '').split(/\s+/);
              const halfW = Math.ceil(words.length / 2);
              return [words.slice(0, halfW).join(' '), words.slice(halfW).join(' ')];
            } catch (e) {
              return [String(text || ''), ''];
            }
          };
          const parts = toTwoBlocks(displayIntro);
          return parts.filter(Boolean).map((p, i) => <Intro key={`intro-part-${i}`}>{p}</Intro>);
        })()}

        <Grid>
          {cfg.images.map((src) => {
            const kind = orients[src] || 'portrait';
            return (
              <Item key={src} className={`item ${kind}`}>
                <Img src={src} alt={cfg.title} loading="lazy" onLoad={onLoad(src)} />
              </Item>
            );
          })}
        </Grid>

        {/* Partners band (small text) */}
        {PARTNERS[resolvedKey] && (
          <PartnersWrap>
            {PARTNERS[resolvedKey].map((p, i) => (
              <div key={`${p.role}-${i}`}>{p.role}: {p.handle}</div>
            ))}
          </PartnersWrap>
        )}

        <Back to={`${location.pathname.startsWith('/fr') ? '/fr' : ''}/portfolio`}>{lang === 'fr' ? '← Retour au Portfolio' : '← Back to Portfolio'}</Back>
      </ContentWrapper>
    </PageContainer>
  );
};

export default GalleryPage;
